import { CSSProperties, ForwardedRef, SyntheticEvent, forwardRef, useContext } from 'react'
import Artboard, { GIF, Image, Sprite, Text } from '../../artboard-gl'
import {
    ArtboardGifLayerProps,
    ArtboardImageLayerProps,
    ArtboardRef,
    ArtboardTextLayerProps,
} from '../../artboard-gl/types'
import { UGEContext } from '../context'
import { UGELayer } from '../types'

type Props = {
    className?: string
    desynchronized?: boolean
    fps?: number
    style?: CSSProperties
    onContextMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>, layer: UGELayer) => void
    onEllipsisMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>, layer: UGELayer) => void
    onRenderError?: (error: Error) => void
}

const UGEArtboardBoard = (
    { className, desynchronized, fps, style, onContextMenu, onEllipsisMenu, onRenderError }: Props,
    ref: ForwardedRef<ArtboardRef>
) => {
    const {
        allowedEditors,
        dimensions,
        duration,
        enableStickers,
        filter,
        filterIntensity,
        layers,
        paused,
        persistArtboardControls,
        reversed,
        showArtboardControls,
        sourceSprite,
        trim,
        openEditor,
        updateLayerPositionById,
    } = useContext(UGEContext)
    const singleStickerMode = allowedEditors.length === 1 && allowedEditors[0] === 'stickers'

    function onGifDoubleClick(layer: UGELayer) {
        if (singleStickerMode) return

        if (layer.searchTerm) {
            openEditor('stickers', { searchTerm: layer.searchTerm })
        }
    }

    function onTextDoubleClick(layer: UGELayer) {
        if (singleStickerMode) return

        openEditor('captions', { layerId: layer.id })
    }

    return (
        <Artboard
            className={className}
            desynchronized={desynchronized}
            duration={duration}
            filter={filter}
            filterIntensity={filterIntensity}
            fps={fps}
            height={dimensions.height}
            key={`uge-artboard-transparency=${enableStickers}`}
            paused={paused}
            ref={ref}
            style={style}
            transparency={enableStickers}
            trim={trim}
            width={dimensions.width}
            onRenderError={onRenderError}
        >
            <Sprite height={dimensions.height} reversed={reversed} source={sourceSprite} width={dimensions.width} />
            {layers.map((layer) => {
                const { props, type } = layer
                const layerKey = `uge-artboard-layer-${layer.id}`
                const defaultProps = {
                    disabled: !showArtboardControls,
                    forceHideControls: showArtboardControls && singleStickerMode && !persistArtboardControls,
                    draggable: true,
                    persistFocus: showArtboardControls && singleStickerMode && persistArtboardControls,
                    x: Math.round(dimensions.width / 2 - props.width / 2),
                    onContextMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>) => onContextMenu(e, layer),
                    onEllipsisMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>) => onEllipsisMenu(e, layer),
                }

                switch (type) {
                    case 'gif':
                    case 'sticker':
                        return (
                            <GIF
                                key={layerKey}
                                {...defaultProps}
                                {...(props as ArtboardGifLayerProps)}
                                onDoubleClick={() => onGifDoubleClick(layer)}
                                onPositionUpdate={(position, isDragging) => {
                                    if (!isDragging) {
                                        updateLayerPositionById(layer.id, position)
                                    }
                                }}
                            />
                        )
                    case 'image':
                        return <Image key={layerKey} {...defaultProps} {...(props as ArtboardImageLayerProps)} />
                    case 'text':
                        return (
                            <Text
                                key={layerKey}
                                {...defaultProps}
                                {...(props as ArtboardTextLayerProps)}
                                onDoubleClick={() => onTextDoubleClick(layer)}
                            />
                        )
                    default:
                        return null
                }
            })}
        </Artboard>
    )
}

export default forwardRef(UGEArtboardBoard)
